import React from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import Spinner from '../../components/common/Spinner/spinner.module';
import type { RootState } from '../../store/store';
import { canAccessSetup, FREEMIUM_ROUTES, isPublicRoute, ROUTE_ACCESS_MAP, ROUTES, SETUP_ACCESS_ROUTES, SETUP_PAID_ACCESS_ROUTES } from '../../utils/routes';
import type { ModuleName } from '../../utils/subscriptions';
import { hasAnyModuleAccess, hasModuleAccess } from '../../utils/subscriptions';
import { getUserFonctionTypesForCompany } from '../../utils/users';
import Page403 from '../../views/Pages/Page403';

interface AccessControlProps {
  children: React.ReactNode;
  navigationList: string[];
  navigationListBO: string[];
}

const AccessControl: React.FC<AccessControlProps> = ({
  children,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const userFunction = useSelector((state: RootState) => state.auth.fonction?.type);
  const user = useSelector((state: RootState) => state.auth.user);
  const company = useSelector((state: RootState) => state.auth.company);
  const isAuthReady = useSelector((state: RootState) => state.auth.isAuthReady);

  const isBlockedAccessPage = matchPath(ROUTES.BLOCKED_ACCESS, currentPath);

  if (isBlockedAccessPage) {
    return <>{children}</>;
  }

  if (isPublicRoute(currentPath)) {
    return <>{children}</>;
  }

  if (!isAuthReady || !company?.id) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-12 text-center shadow-lg">
          <img
            alt="Déclarations Juridiques"
            src={require('../../../img/logo.svg')}
            className="mx-auto w-64"
          />
          <div className="flex flex-col items-center gap-3">
            <Spinner size="xl" />
            <p className="text-sm text-gray-500">Chargement en cours…</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has ROLE_ADMIN
  const isAdmin = user && user.roles.includes('ROLE_ADMIN');

  // If user is admin, allow access to all pages
  if (isAdmin) {
    return <>{children}</>;
  }

  // Restrict setup / account-configuration pages to elevated functions only.
  // This runs before the freemium short-circuit so freemium-listed setup pages
  // (e.g. the parametrage hub) stay gated for standard users.
  const isSetupAccessRoute = SETUP_ACCESS_ROUTES.some(route => matchPath(route, currentPath));
  if (isSetupAccessRoute) {
    const fonctionTypes = getUserFonctionTypesForCompany(user, company?.id);
    if (!canAccessSetup(fonctionTypes, Boolean(isAdmin))) {
      return <Page403 />;
    }
  }

  // Check if current path matches any setup route that requires paid access
  const matchingSetupPaidRoute = SETUP_PAID_ACCESS_ROUTES.find(route => 
    matchPath(route, currentPath),
  );

  // If it's a setup route requiring paid access, check if user has any paid module
  if (matchingSetupPaidRoute && company) {
    const hasPaidAccess = hasAnyModuleAccess(company);
    if (!hasPaidAccess) {
      return <Page403 />;
    }
  }

  // Check if current path matches any freemium route
  const matchingFreemiumRoute = FREEMIUM_ROUTES.find(route => 
    matchPath(route, currentPath),
  );

  // If route is in FREEMIUM_ROUTES, allow access
  if (matchingFreemiumRoute) {
    return <>{children}</>;
  }

  // Find the matching route in ROUTE_ACCESS_MAP
  const matchingRoute = Object.keys(ROUTE_ACCESS_MAP).find(route => 
    matchPath(route, currentPath),
  );

  // If route is in ROUTE_ACCESS_MAP, check user function permissions
  if (matchingRoute) {
    // If user has no function defined, deny access to restricted routes
    if (!userFunction) {
      return <Page403 />;
    }

    // Check if user's function is allowed for this route
    const allowedFunctions = ROUTE_ACCESS_MAP[matchingRoute];
    
    if (!allowedFunctions.includes(userFunction)) {
      return <Page403 />;
    }
  }

  // Check if current path is for a paid module
  // Extract the module name from the path (first segment after the /)
  const pathSegments = currentPath.split('/').filter(Boolean);
  
  if (pathSegments.length > 0) {
    const modulePath = pathSegments[0];
    
    // Map URL path to module name
    let moduleName: ModuleName | null = null;
    if (modulePath === 'communication') {
      moduleName = 'communicating';
    } else if (modulePath === 'prospecting') {
      moduleName = 'prospecting';
    } else if (modulePath === 'monitoring') {
      moduleName = 'monitoring';
    }
    
    // If it's a module path and the user doesn't have access, show 403
    if (moduleName && company) {
      const hasAccess = hasModuleAccess(company, moduleName);
      
      if (!hasAccess) {
        return <Page403 />;
      }
    }
  }

  // Access allowed for all other cases
  return <>{children}</>;
};

export default AccessControl;